import { getBackendClient } from "@/lib/backend";
import { insforge } from "@/lib/insforge";

export type FinanceCommandKind =
  | "customer_payment"
  | "estimate_payment"
  | "vendor_payment"
  | "customer_credit_application";

interface PendingCommand {
  fingerprint: string;
  operationId: string;
}

export interface FinanceCommandExecution {
  acknowledge: () => void;
  result: unknown;
}

interface Resolution {
  result?: unknown;
  state: "absent" | "cancelled" | "completed" | "pending";
}

function storageKey(userId: string, kind: FinanceCommandKind): string {
  return "xan:finance-command:v1:" + userId + ":" + kind;
}

function readPending(key: string): PendingCommand | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    throw new Error("Browser storage is unavailable. Payment attempts cannot be saved safely.");
  }
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<PendingCommand>;
    if (typeof value.fingerprint === "string" && typeof value.operationId === "string") {
      return { fingerprint: value.fingerprint, operationId: value.operationId };
    }
  } catch {
    // A broken entry cannot safely identify the earlier financial command.
  }
  throw new Error("A saved payment attempt is unreadable. Contact support before trying again.");
}

async function fingerprint(payload: Record<string, unknown>): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function resolve(
  kind: FinanceCommandKind,
  operationId: string,
  sealAbsent: boolean,
): Promise<Resolution> {
  const { data, error } = await getBackendClient().data.rpc<Resolution>(
    "xan_resolve_finance_command",
    { p_kind: kind, p_operation_id: operationId, p_seal_absent: sealAbsent },
  );
  if (error) throw new Error(error.message);
  if (!data || !["absent", "cancelled", "completed", "pending"].includes(data.state)) {
    throw new Error("The payment attempt could not be resolved.");
  }
  return data;
}

function clearIfCurrent(key: string, operationId: string): void {
  if (readPending(key)?.operationId === operationId) {
    localStorage.removeItem(key);
  }
}

const inFlight = new Map<
  string,
  { fingerprint: string; promise: Promise<FinanceCommandExecution> }
>();

async function resolveSavedCommand(
  key: string,
  kind: FinanceCommandKind,
  pending: PendingCommand,
  nextFingerprint: string,
): Promise<FinanceCommandExecution | "new" | "reuse"> {
  const status = await resolve(kind, pending.operationId, false);
  if (status.state === "completed") {
    if (pending.fingerprint === nextFingerprint) {
      return {
        result: status.result,
        acknowledge: () => clearIfCurrent(key, pending.operationId),
      };
    }
    clearIfCurrent(key, pending.operationId);
    throw new Error(
      "The previous payment completed. Review it before recording a changed payment.",
    );
  }
  if (status.state === "pending") {
    throw new Error("The previous payment is still processing. Try again shortly.");
  }
  if (status.state === "absent" && pending.fingerprint === nextFingerprint) {
    return "reuse";
  }

  const resolved =
    status.state === "absent" ? await resolve(kind, pending.operationId, true) : status;
  if (resolved.state === "completed") {
    clearIfCurrent(key, pending.operationId);
    throw new Error(
      "The previous payment completed. Review it before recording a changed payment.",
    );
  }
  if (resolved.state !== "cancelled") {
    throw new Error("The previous payment could not be resolved.");
  }
  clearIfCurrent(key, pending.operationId);
  return "new";
}

async function runCommand(
  key: string,
  kind: FinanceCommandKind,
  payload: Record<string, unknown>,
  nextFingerprint: string,
): Promise<FinanceCommandExecution> {
  const pending = readPending(key);
  let operationId = pending?.operationId;

  if (pending) {
    const resolution = await resolveSavedCommand(key, kind, pending, nextFingerprint);
    if (resolution === "new") {
      operationId = undefined;
    } else if (resolution !== "reuse") {
      return resolution;
    }
  }

  if (!operationId) {
    operationId = crypto.randomUUID();
    try {
      localStorage.setItem(key, JSON.stringify({ fingerprint: nextFingerprint, operationId }));
    } catch {
      throw new Error("Browser storage is unavailable. Payment attempts cannot be saved safely.");
    }
  }

  const { data, error } = await getBackendClient().data.rpc("xan_execute_finance_command", {
    p_kind: kind,
    p_operation_id: operationId,
    p_payload: payload,
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("The payment attempt returned no result. Retry the same command.");
  return { result: data, acknowledge: () => clearIfCurrent(key, operationId) };
}

/**
 * One unresolved identity is kept per caller and command kind. The browser
 * stores only an operation UUID and a payload hash. A changed draft first
 * seals an absent identity so a delayed request cannot commit afterward.
 */
export async function executeFinanceCommand(
  kind: FinanceCommandKind,
  payload: Record<string, unknown>,
): Promise<FinanceCommandExecution> {
  const { data: identity, error: identityError } = await insforge.auth.getCurrentUser();
  const userId = identity?.user?.id;
  if (identityError || !userId) {
    throw new Error("Sign in again before recording this payment.");
  }

  const key = storageKey(userId, kind);
  const nextFingerprint = await fingerprint(payload);
  const active = inFlight.get(key);
  if (active) {
    if (active.fingerprint !== nextFingerprint) {
      throw new Error("Another payment is processing. Resolve it before changing this payment.");
    }
    return active.promise;
  }

  const promise = runCommand(key, kind, payload, nextFingerprint);
  inFlight.set(key, { fingerprint: nextFingerprint, promise });
  try {
    return await promise;
  } finally {
    if (inFlight.get(key)?.promise === promise) inFlight.delete(key);
  }
}

/** Check a saved attempt before balance-based form guards can hide its result. */
export async function recoverFinanceCommand(
  kind: FinanceCommandKind,
): Promise<FinanceCommandExecution | null> {
  const prefix = "xan:finance-command:v1:";
  if (
    !Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).some(
      (key) => key?.startsWith(prefix),
    )
  )
    return null;
  const { data: identity, error } = await insforge.auth.getCurrentUser();
  const userId = identity?.user?.id;
  if (error || !userId) return null;
  const key = storageKey(userId, kind);
  const pending = readPending(key);
  if (!pending) return null;
  const status = await resolve(kind, pending.operationId, false);
  if (status.state === "cancelled") {
    clearIfCurrent(key, pending.operationId);
    return null;
  }
  if (status.state !== "completed") return null;
  return {
    result: status.result,
    acknowledge: () => clearIfCurrent(key, pending.operationId),
  };
}
